
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { User, UserStatus, UserRole } from '../types';

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const initAdmin = async () => {
      // Try to get cached user first
      let user = authService.getCurrentUser();

      // If no cached user, try to refresh from server
      if (!user) {
        user = await authService.refreshUser();
      }

      // If still no user or not an admin, redirect to login
      if (!user || user.role !== UserRole.ADMIN) {
        navigate('/login');
        return;
      }

      setCurrentUser(user);
      loadUsers();
    };

    initAdmin();
  }, [navigate]);

  const loadUsers = async () => {
    const fetchedUsers = await authService.getAllUsers();
    setUsers(fetchedUsers);
  };

  const handleStatusChange = async (userId: string, newStatus: string) => {
    const result = await authService.updateUserStatus(userId, newStatus as UserStatus);
    if (result.success) {
      loadUsers(); // Refresh list
    } else {
      alert(result.message || 'Failed to update user status');
    }
  };

  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE: return 'bg-green-100 text-green-700 border-green-200';
      case UserStatus.ONBOARDING: return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case UserStatus.INACTIVE: return 'bg-slate-100 text-slate-500 border-slate-200';
      default: return 'bg-slate-100';
    }
  };

  if (!currentUser) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-indigo-900 brand-font">User Management</h1>
          <p className="text-slate-500">Manage student accounts and access</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/ai-report')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors flex items-center"
          >
            <i className="fas fa-robot mr-2"></i>
            Weekly AI Report
          </button>
          <div className="bg-indigo-100 text-brand-blue px-4 py-2 rounded-lg font-bold">
            Admin Mode
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">User</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Grade/State</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Tests Taken</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-brand-light text-brand-blue flex items-center justify-center font-bold mr-3">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${user.role === UserRole.ADMIN ? 'bg-purple-100 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {user.role === UserRole.STUDENT ? `Grade ${user.grade} • ${user.state}` : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-semibold text-slate-700">
                      {user.quizCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {user.id !== currentUser.id ? (
                      <select
                        value={user.status}
                        onChange={(e) => handleStatusChange(user.id, e.target.value)}
                        className="bg-white border border-slate-300 text-slate-700 text-sm rounded-lg px-2 py-1 focus:ring-brand-blue focus:border-brand-blue outline-none cursor-pointer"
                      >
                        <option value={UserStatus.ONBOARDING}>{UserStatus.ONBOARDING}</option>
                        <option value={UserStatus.ACTIVE}>{UserStatus.ACTIVE}</option>
                        <option value={UserStatus.INACTIVE}>{UserStatus.INACTIVE}</option>
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Current User</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
